import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Button } from "@relais/design-system";
import { FileText, Upload } from "lucide-react";
import {
  INVOICE_REVIEW_STATUS_LABELS_FR,
  defaultDepositRoles,
  groupDeposit,
  type DepositRole,
  type InvoiceReviewStatus,
  type RelaisInvoiceDetail,
} from "@relais/shared";
import { BackLink } from "../components/BackLink.js";
import { PROFIL_CARD } from "../lib/profil-theme.js";
import { useOffline } from "../lib/offline-context.js";
import { INVOICE_FILE_ERROR_FR, validateInvoiceFile } from "../lib/invoice-file.js";
import { fetchMyInvoices, uploadMyInvoice } from "../lib/invoices.js";

export const Route = createFileRoute("/profil_/factures")({
  component: InvoicesPage,
});

// Ce que le relais voit de son côté : l'état de traitement, pas le verdict du
// contrôle. Le contrôle automatique est un outil interne — annoncer « rejetée »
// à un relais sur la foi d'une lecture de PDF serait faux et blessant. Seule la
// décision prise par Greenkub (review_status) lui est montrée.
const STATUS_STYLE: Record<InvoiceReviewStatus, string> = {
  pending: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100",
  accepted: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100",
  rejected: "bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-100",
};

const STATUS_LABEL_RELAIS: Record<InvoiceReviewStatus, string> = {
  pending: "En cours de traitement",
  accepted: INVOICE_REVIEW_STATUS_LABELS_FR.accepted,
  rejected: INVOICE_REVIEW_STATUS_LABELS_FR.rejected,
};

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function fmtEur(n: number | null): string {
  if (n === null) return "—";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n);
}

function InvoicesPage() {
  const { connectionState } = useOffline();
  const online = connectionState === "online";

  const [invoices, setInvoices] = useState<RelaisInvoiceDetail[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [uploading, setUploading] = useState<{ done: number; total: number } | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [justSent, setJustSent] = useState(0);
  const [picked, setPicked] = useState<File[]>([]);
  const [roles, setRoles] = useState<DepositRole[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  async function load() {
    try {
      setInvoices(await fetchMyInvoices());
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }

  useEffect(() => {
    if (online) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  // Le choix des fichiers ne déclenche pas l'envoi : le relais joint souvent un
  // itinéraire à sa facture, et doit pouvoir dire lequel est quoi avant que ça
  // parte. Le rôle est pré-rempli d'après le nom du fichier.
  function onPicked(files: File[]) {
    const failures: string[] = [];
    const valid: File[] = [];
    for (const file of files) {
      const invalid = validateInvoiceFile(file);
      if (invalid) failures.push(`${file.name} : ${INVOICE_FILE_ERROR_FR[invalid]}`);
      else valid.push(file);
    }
    setErrors(failures);
    setJustSent(0);
    setPicked(valid);
    setRoles(defaultDepositRoles(valid.map((f) => f.name)));
    if (inputRef.current) inputRef.current.value = "";
  }

  function setRole(index: number, value: string) {
    setRoles((current) =>
      current.map((role, i) =>
        i === index
          ? value === "facture"
            ? { kind: "facture" }
            : { kind: "annexe", parent: Number(value) }
          : role,
      ),
    );
  }

  // Les factures partent À LA SUITE, pas en parallèle : chaque dépôt déclenche
  // le contrôle côté serveur, et un envoi qui échoue ne doit pas emporter les
  // autres — d'où une erreur par facture plutôt qu'une erreur globale.
  async function send() {
    const groups = groupDeposit(picked, roles);
    if (groups.length === 0) return;

    const failures: string[] = [];
    let sent = 0;
    setErrors([]);
    setUploading({ done: 0, total: groups.length });
    for (const group of groups) {
      try {
        await uploadMyInvoice(group.invoice, group.attachments);
        sent += 1;
        setJustSent(sent);
      } catch (e) {
        failures.push(
          e instanceof Error && e.message.startsWith("upload_failed")
            ? `${group.invoice.name} : l'envoi a échoué. Vérifiez votre connexion et réessayez.`
            : `${group.invoice.name} : impossible d'enregistrer la facture. Réessayez dans un instant.`,
        );
        setErrors([...failures]);
      } finally {
        setUploading({ done: sent + failures.length, total: groups.length });
      }
    }

    setUploading(null);
    setPicked([]);
    setRoles([]);
    await load();
  }

  return (
    <div className="flex flex-1 flex-col gap-6 pb-2">
      <BackLink to="/profil">Profil</BackLink>
      <header>
        <h1 className="text-2xl font-semibold text-foreground">Vos factures</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Déposez vos factures en PDF, avec les itinéraires qui les justifient.
          Elles arrivent directement au service facturation de Greenkub.
        </p>
      </header>

      <section className={"space-y-3 rounded-xl p-4 " + PROFIL_CARD}>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="application/pdf,.pdf"
          className="hidden"
          aria-label="Factures PDF"
          onChange={(e) => {
            const picked = Array.from(e.target.files ?? []);
            if (picked.length > 0) void onPicked(picked);
          }}
        />
        <Button
          className="h-12 w-full text-base font-medium"
          disabled={!online || uploading !== null}
          onClick={() => inputRef.current?.click()}
          variant={picked.length > 0 ? "outline" : "default"}
        >
          <Upload className="mr-2 size-5" aria-hidden />
          {uploading
            ? uploading.total > 1
              ? `Envoi en cours… (${uploading.done} sur ${uploading.total})`
              : "Envoi en cours…"
            : picked.length > 0
              ? "Choisir d'autres fichiers"
              : "Choisir une ou plusieurs factures"}
        </Button>

        {picked.length > 0 && !uploading && (
          <>
            <ul className="space-y-2">
              {picked.map((file, i) => (
                <li key={file.name + i} className="space-y-1">
                  <p className="truncate text-sm text-foreground" title={file.name}>
                    {file.name}
                  </p>
                  <select
                    value={roles[i]?.kind === "annexe" ? String(roles[i]!.parent) : "facture"}
                    onChange={(e) => setRole(i, e.target.value)}
                    aria-label={`Ce fichier est : ${file.name}`}
                    className="h-10 w-full rounded-lg border bg-background px-3 text-sm"
                  >
                    <option value="facture">C'est une facture</option>
                    {picked.map((parent, j) =>
                      j !== i && roles[j]?.kind === "facture" ? (
                        <option key={j} value={j}>
                          Pièce jointe à {parent.name}
                        </option>
                      ) : null,
                    )}
                  </select>
                </li>
              ))}
            </ul>
            <Button
              className="h-12 w-full text-base font-medium"
              disabled={!online}
              onClick={() => void send()}
            >
              Envoyer
            </Button>
          </>
        )}

        {!online && (
          <p className="text-sm text-muted-foreground">
            Le dépôt d'une facture nécessite une connexion. Vos factures peuvent
            attendre votre retour de chantier.
          </p>
        )}
        {errors.map((e) => (
          <p key={e} className="text-sm text-destructive">
            {e}
          </p>
        ))}
        {justSent > 0 && !uploading && (
          <p className="text-sm text-emerald-700 dark:text-emerald-400">
            {justSent > 1
              ? `${justSent} factures reçues. Greenkub les traite et revient vers vous.`
              : "Facture reçue. Greenkub la traite et revient vers vous."}
          </p>
        )}
      </section>

      {loadError && online && (
        <p className="text-sm text-destructive">
          Impossible de charger vos factures.
        </p>
      )}
      {!online && invoices === null && (
        <p className="text-sm text-muted-foreground">
          Vos factures ne sont pas consultables hors connexion.
        </p>
      )}

      {invoices?.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Vous n'avez encore déposé aucune facture.
        </p>
      )}

      <ul className="space-y-2">
        {(invoices ?? []).map((inv) => (
          <li
            key={inv.id}
            className={"flex items-center gap-3 rounded-xl p-3 " + PROFIL_CARD}
          >
            <FileText className="size-5 shrink-0 text-muted-foreground" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">
                {inv.invoice_number ?? inv.file_name}
              </p>
              <p className="text-xs text-muted-foreground">
                Déposée le {fmtDate(inv.uploaded_at)}
                {inv.amount_ht !== null && ` · ${fmtEur(inv.amount_ht)} HT`}
                {inv.attachments.length > 0 &&
                  ` · ${inv.attachments.length} pièce${
                    inv.attachments.length > 1 ? "s" : ""
                  } jointe${inv.attachments.length > 1 ? "s" : ""}`}
              </p>
              {inv.review_status === "rejected" && inv.review_note && (
                <p className="mt-1 text-xs text-red-700 dark:text-red-300">
                  {inv.review_note}
                </p>
              )}
            </div>
            <span
              className={
                "shrink-0 rounded px-2 py-0.5 text-xs font-medium " +
                STATUS_STYLE[inv.review_status]
              }
            >
              {STATUS_LABEL_RELAIS[inv.review_status]}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
